import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, themeQuartz } from "ag-grid-community";
import type { CustomCellRendererProps } from "ag-grid-react";
import { TrashIcon, PlusIcon, PencilIcon } from "lucide-react";

import { Button } from "@/lib/shadcn/ui/button";
import { Input } from "@/lib/shadcn/ui/input";
import { Label } from "@/lib/shadcn/ui/label";
import { SearchableSelect } from "@/lib/shadcn/components/searchable-select";
import {
    useGetAddressesWithCountsQuery,
    useGetMunicipalitySectionsQuery,
    useAddAddressMutation,
    useUpdateAddressMutation,
    useDeleteAddressMutation,
} from "@/store/booking-management-api";
import { ApartmentAtAddress } from "@/models/responses";
import { DeleteOptionConfirmDialog } from "@/lib/shadcn/components/delete-option-confirm-dialog";
import {
    BlockingApartment,
    DeleteOptionBlockedDialog,
} from "@/lib/shadcn/components/delete-option-blocked-dialog";
import { extractErrorMessage, isAddressInUseError } from "@/utils/inUseError";

interface AddressRow {
    id: number;
    street: string;
    number: string;
    postalCode: string;
    municipalitySection: string;
    apartmentCount: number;
    apartments: ApartmentAtAddress[];
}

/** A building is identified by its street + number, never by its id, in operator-facing copy. */
function addressLabel(row: AddressRow): string {
    return `${row.street} ${row.number}`;
}

const ADD_BUTTON_TESTID = "addresses-management-add-button";
const STREET_ID = "addresses-management-street";
const NUMBER_ID = "addresses-management-number";
const POSTAL_ID = "addresses-management-postal-code";
const SECTION_ID = "addresses-management-municipality-section";

const editAddressButtonTestId = (addressId: number): string => `edit-address-button-${addressId}`;

interface AddressFormFields {
    street: string;
    number: string;
    postalCode: string;
    sectionId: string;
}

/**
 * One form serves both registering and correcting a building: the fields and the
 * validation are identical, and the server re-checks folded-identity uniqueness
 * either way (address-normalization/FR-002, /FR-003). What differs is only
 * `mode` — which is why it is ONE discriminated value rather than a boolean and
 * an id that can disagree about whether this is an add or an edit.
 *
 * `label` is the building's identity AS THE FORM OPENED, so the announcement
 * naming it does not change under the operator while they retype the street.
 */
type AddressFormState =
    | { mode: "add"; fields: AddressFormFields; error?: string }
    | { mode: "edit"; id: number; label: string; fields: AddressFormFields; error?: string };

const EMPTY_FIELDS: AddressFormFields = { street: "", number: "", postalCode: "", sectionId: "" };

/** Rebuild the state around new fields/error without losing the mode it is in. */
function withForm(
    state: AddressFormState,
    change: { fields?: Partial<AddressFormFields>; error?: string },
): AddressFormState {
    const fields = { ...state.fields, ...change.fields };
    return state.mode === "add"
        ? { mode: "add", fields, error: change.error }
        : { mode: "edit", id: state.id, label: state.label, fields, error: change.error };
}

function formTitle(state: AddressFormState): string {
    return state.mode === "add" ? "Add a new address" : `Edit address ${state.label}`;
}

interface ApartmentCountRendererProps extends CustomCellRendererProps<AddressRow> {
    onToggle: (row: AddressRow) => void;
}

/**
 * The count doubles as the affordance for naming the apartments at the building
 * (address-normalization/AS-008) — the operator who wants to know *which*
 * apartments block a removal reaches for the number first.
 */
const ApartmentCountRenderer: React.FC<ApartmentCountRendererProps> = ({ data, onToggle }) => {
    if (!data) return null;
    return (
        <Button
            variant="ghost"
            size="sm"
            aria-label={`Show apartments at ${addressLabel(data)}`}
            data-testid={`addresses-management-apartments-button-${data.id}`}
            onClick={() => onToggle(data)}
            className="h-8 px-2"
        >
            {data.apartmentCount}
        </Button>
    );
};

interface ActionsRendererProps extends CustomCellRendererProps<AddressRow> {
    onEdit: (row: AddressRow) => void;
    onDelete: (row: AddressRow) => void;
}

const ActionsRenderer: React.FC<ActionsRendererProps> = ({ data, onEdit, onDelete }) => {
    if (!data) return null;
    return (
        <div className="text-right">
            <Button
                variant="ghost"
                size="sm"
                aria-label={`Edit address ${addressLabel(data)}`}
                data-testid={editAddressButtonTestId(data.id)}
                onClick={() => onEdit(data)}
                className="h-8 w-8 p-0"
            >
                <PencilIcon className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
                variant="ghost"
                size="sm"
                aria-label={`Delete address ${addressLabel(data)}`}
                data-testid={`delete-address-button-${data.id}`}
                onClick={() => onDelete(data)}
                className="text-destructive hover:text-destructive h-8 w-8 p-0"
            >
                <TrashIcon className="h-4 w-4" aria-hidden="true" />
            </Button>
        </div>
    );
};

const AddressesManagementPage: React.FC = () => {
    const { data: addresses } = useGetAddressesWithCountsQuery();
    const { data: sections } = useGetMunicipalitySectionsQuery();
    const [addAddress] = useAddAddressMutation();
    const [updateAddress] = useUpdateAddressMutation();
    const [deleteAddress] = useDeleteAddressMutation();

    const [formState, setFormState] = useState<AddressFormState | undefined>(undefined);
    const streetRef = useRef<HTMLInputElement>(null);
    // What opened the current form session, so the keyboard can be handed back
    // when it closes. Held as a testid rather than an element because BOTH
    // openers unmount while the form is up (the "Add address" button is replaced
    // by the form; a row's Edit button can be scrolled away or removed), so the
    // element captured on the way in is not the one to focus on the way out.
    const returnFocusTestIdRef = useRef<string | undefined>(undefined);

    const [expandedAddressId, setExpandedAddressId] = useState<number | undefined>(undefined);

    const [pendingDelete, setPendingDelete] = useState<AddressRow | undefined>(undefined);
    const [isDeleting, setIsDeleting] = useState(false);
    const [blockedDialogState, setBlockedDialogState] = useState<
        { addressName: string; blockingApartments: BlockingApartment[] } | undefined
    >(undefined);
    const [deleteError, setDeleteError] = useState<string | undefined>(undefined);

    const sectionNamesById = useMemo(() => {
        const byId = new Map<number, string>();
        for (const section of sections ?? []) {
            byId.set(section.id, section.name);
        }
        return byId;
    }, [sections]);

    const rows: AddressRow[] = useMemo(
        () =>
            (addresses ?? []).map((address) => ({
                id: address.id,
                street: address.street,
                number: address.number,
                postalCode: address.postalCode,
                municipalitySection: sectionNamesById.get(address.municipalitySectionId) ?? "",
                apartmentCount: address.apartments.length,
                apartments: address.apartments,
            })),
        [addresses, sectionNamesById],
    );

    const expandedRow = useMemo(
        () => rows.find((row) => row.id === expandedAddressId),
        [rows, expandedAddressId],
    );

    const sectionOptions = useMemo(
        () => (sections ?? []).map((s) => ({ value: String(s.id), label: s.name })),
        [sections],
    );

    const handleToggleApartments = useCallback((row: AddressRow) => {
        setExpandedAddressId((current) => (current === row.id ? undefined : row.id));
    }, []);

    const handleDeleteClick = useCallback((row: AddressRow) => {
        setPendingDelete(row);
        setDeleteError(undefined);
    }, []);

    // Open on the building AS RECORDED — the operator corrects one field, they do
    // not retype the address. The section is matched by id, not by name, so a
    // renamed section still resolves.
    const handleEditClick = useCallback(
        (row: AddressRow) => {
            const address = (addresses ?? []).find((a) => a.id === row.id);
            returnFocusTestIdRef.current = editAddressButtonTestId(row.id);
            setFormState({
                mode: "edit",
                id: row.id,
                label: addressLabel(row),
                fields: {
                    street: row.street,
                    number: row.number,
                    postalCode: row.postalCode,
                    sectionId: address ? String(address.municipalitySectionId) : "",
                },
            });
        },
        [addresses],
    );

    // Opening the form unmounts the button that opened it, so the keyboard would
    // otherwise fall back to `document.body`. Landing it in the first field also
    // announces the group it sits in — which is what tells an operator who cannot
    // see the toolbar that a form appeared, and whether it is adding or
    // correcting a named building (WCAG 2.4.3 / 4.1.3).
    //
    // Closing it has to be symmetric, on EVERY way out — a successful save, a
    // Cancel, and the session being force-closed because its row disappeared —
    // or the operator is dropped on `document.body` with the field they were in
    // unmounted.
    const formSession =
        formState === undefined ? undefined : formState.mode === "add" ? "add" : `edit:${formState.id}`;
    useEffect(() => {
        if (formSession !== undefined) {
            streetRef.current?.focus();
            return;
        }
        const target = returnFocusTestIdRef.current;
        if (target === undefined) {
            return;
        }
        returnFocusTestIdRef.current = undefined;
        // The row's Edit button is gone when the building being corrected was the
        // one just removed. "Add address" is the control that takes its place in
        // the same toolbar slot, so it is the sensible surviving target.
        const opener =
            document.querySelector<HTMLElement>(`[data-testid="${target}"]`) ??
            document.querySelector<HTMLElement>(`[data-testid="${ADD_BUTTON_TESTID}"]`);
        opener?.focus();
    }, [formSession]);

    // An edit session must not outlive its row: the form still holds the
    // building's values, and a later Save would PUT to something that is gone.
    useEffect(() => {
        if (addresses === undefined || formState?.mode !== "edit") return;
        if (!addresses.some((address) => address.id === formState.id)) {
            setFormState(undefined);
        }
    }, [addresses, formState]);

    const columnDefs: ColDef<AddressRow>[] = useMemo(
        () => [
            {
                field: "street",
                headerName: "Street",
                cellDataType: "text",
                flex: 2,
                sortable: true,
                sort: "asc",
                filter: "agTextColumnFilter",
            },
            {
                field: "number",
                headerName: "Number",
                cellDataType: "text",
                width: 120,
                sortable: true,
                filter: "agTextColumnFilter",
            },
            {
                field: "postalCode",
                headerName: "Postal Code",
                cellDataType: "text",
                width: 160,
                sortable: true,
                filter: "agTextColumnFilter",
            },
            {
                field: "municipalitySection",
                headerName: "Municipality Section",
                cellDataType: "text",
                flex: 1,
                sortable: true,
                filter: "agTextColumnFilter",
            },
            {
                field: "apartmentCount",
                headerName: "Apartments",
                cellDataType: "number",
                width: 150,
                sortable: true,
                filter: "agNumberColumnFilter",
                cellRenderer: ApartmentCountRenderer,
                cellRendererParams: {
                    onToggle: handleToggleApartments,
                },
            },
            {
                field: "actions" as keyof AddressRow,
                headerName: "Actions",
                cellRenderer: ActionsRenderer,
                cellRendererParams: {
                    onEdit: handleEditClick,
                    onDelete: handleDeleteClick,
                },
                sortable: false,
                filter: false,
                width: 140,
            },
        ],
        [handleDeleteClick, handleEditClick, handleToggleApartments],
    );

    const defaultColDef = useMemo<ColDef<AddressRow>>(
        () => ({
            resizable: true,
            sortable: true,
            editable: false,
            cellStyle: {
                textAlign: "left",
                paddingLeft: "18px",
                fontSize: "13px",
            },
        }),
        [],
    );

    // Typing anywhere in the form also clears the last refusal — the operator is
    // already acting on it.
    const editField = useCallback((key: keyof AddressFormFields, value: string) => {
        setFormState((current) => (current === undefined ? current : withForm(current, { fields: { [key]: value } })));
    }, []);

    const onAddSubmit = useCallback(async () => {
        if (formState === undefined) return;
        const street = formState.fields.street.trim();
        const number = formState.fields.number.trim();
        const postalCode = formState.fields.postalCode.trim();
        const fail = (error: string) => setFormState((current) => (current === undefined ? current : withForm(current, { error })));
        if (!street || !number || !postalCode) {
            fail("Street, number and postal code are required");
            return;
        }
        if (!formState.fields.sectionId) {
            fail("Municipality section is required");
            return;
        }
        const payload = {
            street,
            number,
            postalCode,
            municipalitySectionId: Number(formState.fields.sectionId),
        };
        try {
            if (formState.mode === "add") {
                await addAddress(payload).unwrap();
            } else {
                await updateAddress({ id: formState.id, ...payload }).unwrap();
            }
            setFormState(undefined);
        } catch (err) {
            // The form stays open on a refusal: a folded-identity collision
            // (FR-003) is the operator's to resolve, and the values they typed
            // are what they need in front of them to resolve it.
            fail(
                extractErrorMessage(err) ??
                    (formState.mode === "add" ? "Could not add address" : "Could not save address"),
            );
        }
    }, [addAddress, formState, updateAddress]);

    const onConfirmDelete = useCallback(async () => {
        if (!pendingDelete) return;
        setIsDeleting(true);
        setDeleteError(undefined);
        const target = pendingDelete;
        try {
            await deleteAddress({ id: target.id }).unwrap();
            setPendingDelete(undefined);
            setExpandedAddressId((current) => (current === target.id ? undefined : current));
        } catch (err) {
            if (isAddressInUseError(err)) {
                const entry = err.errors[0];
                setBlockedDialogState({
                    addressName: addressLabel(target),
                    blockingApartments: entry.blockingApartments.map((b) => ({
                        apartmentId: b.apartmentId,
                        apartmentName: b.apartmentName,
                    })),
                });
                setPendingDelete(undefined);
            } else {
                setDeleteError("Could not delete address");
                setPendingDelete(undefined);
            }
        } finally {
            setIsDeleting(false);
        }
    }, [deleteAddress, pendingDelete]);

    return (
        <div data-testid="addresses-management-page" className="flex flex-col gap-2 p-4">
            <div className="flex min-w-0 items-center justify-between">
                <h1 className="text-lg font-semibold text-gray-900">Addresses</h1>
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                    {formState !== undefined ? (
                        // A named group, not a bare row of inputs: add mode and edit
                        // mode are the same markup, so the name is the only thing
                        // that tells an operator which one they are in and which
                        // building they are correcting.
                        <div
                            role="group"
                            aria-label={formTitle(formState)}
                            data-testid="addresses-management-form"
                            className="flex min-w-0 flex-wrap items-end gap-2"
                        >
                            {/*
                             * Each field carries a PERSISTENT visible label, not just
                             * a placeholder: a placeholder disappears the moment the
                             * operator types, taking with it the only clue to which
                             * of three short boxes they are in (WCAG 3.3.2). They are
                             * stacked and compact so the toolbar row still reads as a
                             * row; `items-end` keeps the inputs and buttons on one
                             * baseline despite the added label line.
                             */}
                            <div className="flex flex-col gap-1">
                                <Label htmlFor={STREET_ID} className="text-xs text-gray-600">
                                    Street*
                                </Label>
                                <Input
                                    id={STREET_ID}
                                    ref={streetRef}
                                    data-testid="addresses-management-add-street"
                                    aria-required={true}
                                    value={formState.fields.street}
                                    onChange={(e) => editField("street", e.target.value)}
                                    className="w-56"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <Label htmlFor={NUMBER_ID} className="text-xs text-gray-600">
                                    Number*
                                </Label>
                                <Input
                                    id={NUMBER_ID}
                                    data-testid="addresses-management-add-number"
                                    aria-required={true}
                                    value={formState.fields.number}
                                    onChange={(e) => editField("number", e.target.value)}
                                    className="w-24"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <Label htmlFor={POSTAL_ID} className="text-xs text-gray-600">
                                    Postal code*
                                </Label>
                                <Input
                                    id={POSTAL_ID}
                                    data-testid="addresses-management-add-postal-code"
                                    aria-required={true}
                                    value={formState.fields.postalCode}
                                    onChange={(e) => editField("postalCode", e.target.value)}
                                    className="w-32"
                                />
                            </div>
                            <div
                                data-testid="addresses-management-add-municipality-section"
                                className="flex flex-col gap-1"
                            >
                                {/*
                                 * Labelled like its neighbours, and by the SAME
                                 * mechanism: an `aria-label` here would silently
                                 * override the visible text and hide the required
                                 * marker the sighted operator sees.
                                 */}
                                <Label htmlFor={SECTION_ID} className="text-xs text-gray-600">
                                    Municipality section*
                                </Label>
                                <SearchableSelect
                                    id={SECTION_ID}
                                    options={sectionOptions}
                                    selectedValue={formState.fields.sectionId}
                                    onChange={(value) => editField("sectionId", value)}
                                    placeholder="Municipality section"
                                    variant="outline"
                                    width="w-56"
                                    aria-required={true}
                                />
                            </div>
                            <Button
                                size="sm"
                                data-testid="addresses-management-add-submit"
                                onClick={() => void onAddSubmit()}
                            >
                                {formState.mode === "add" ? "Add" : "Save"}
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                data-testid="addresses-management-form-cancel"
                                onClick={() => setFormState(undefined)}
                            >
                                Cancel
                            </Button>
                        </div>
                    ) : (
                        <Button
                            size="sm"
                            data-testid={ADD_BUTTON_TESTID}
                            onClick={() => {
                                returnFocusTestIdRef.current = ADD_BUTTON_TESTID;
                                setFormState({ mode: "add", fields: EMPTY_FIELDS });
                            }}
                        >
                            <PlusIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />
                            Add address
                        </Button>
                    )}
                </div>
            </div>

            {formState?.error !== undefined && (
                <div
                    data-testid="addresses-management-add-error"
                    role="alert"
                    className="text-sm text-destructive"
                >
                    {formState.error}
                </div>
            )}
            {deleteError && (
                <div
                    data-testid="addresses-management-delete-error"
                    role="alert"
                    className="text-sm text-destructive"
                >
                    {deleteError}
                </div>
            )}

            {expandedRow && (
                <div
                    data-testid={`addresses-management-apartments-${expandedRow.id}`}
                    className="rounded-md border border-gray-200 p-3 text-sm"
                >
                    <div className="mb-1 font-medium text-gray-900">
                        Apartments at {addressLabel(expandedRow)}
                    </div>
                    {expandedRow.apartments.length === 0 ? (
                        <div className="text-gray-500">No apartments at this address</div>
                    ) : (
                        <ul className="flex flex-col gap-0.5">
                            {expandedRow.apartments.map((apartment) => (
                                <li
                                    key={apartment.apartmentId}
                                    data-testid={`address-apartment-${apartment.apartmentId}`}
                                >
                                    {apartment.apartmentName}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            <div className="ag-theme-quartz h-[700px] rounded-md border border-gray-200">
                <AgGridReact<AddressRow>
                    theme={themeQuartz}
                    rowData={rows}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    getRowId={(params) => params.data.id.toString()}
                    animateRows={true}
                />
            </div>

            <DeleteOptionConfirmDialog
                optionTypeLabel="address"
                optionName={pendingDelete ? addressLabel(pendingDelete) : ""}
                open={pendingDelete !== undefined}
                onOpenChange={(open) => {
                    if (!open) setPendingDelete(undefined);
                }}
                onConfirm={() => void onConfirmDelete()}
                isDeleting={isDeleting}
            />
            <DeleteOptionBlockedDialog
                optionTypeLabel="address"
                optionName={blockedDialogState?.addressName ?? ""}
                blockingApartments={blockedDialogState?.blockingApartments ?? []}
                open={blockedDialogState !== undefined}
                onOpenChange={(open) => {
                    if (!open) setBlockedDialogState(undefined);
                }}
            />
        </div>
    );
};

export default AddressesManagementPage;
